import { Alert, Box, Stack } from '@mui/material';
import { sub } from 'date-fns';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDevice, useDeviceSystemConfigurations, useDeviceSystemErrors } from '@/api/devices';
import { DeviceStatusHistoryItem, useDeviceStatusHistory } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { AlertLine } from '@/charts/types';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { ChartDataDetails, ChartWithInfo } from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { Timeframe, TimeframeButtonGroup } from '@/components/spotter/notificationTab/TimeframeButtonGroup';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FixedDateRangeContextProvider, useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useTzContext } from '@/hooks/useTzContext';

type LineConfig = Line<DeviceStatusHistoryItem>;

export const BATTERY_VOLTAGE_LINE = {
    id: 'battery-voltage',
    label: 'Battery voltage',
    valueKey: 'batteryVoltage' as const,
    color: colorsets.blue,
    unit: 'V',
    unitType: 'voltage',
} satisfies LineConfig;

const LINES = [BATTERY_VOLTAGE_LINE];

export type BatteryVoltageChartProps = {
    chartTitle: string;
    spotterId: string;
    alertLines?: AlertLine[];
    lines: LineConfig[];
    onSelectData?: (data: LineDataSlice<DeviceStatusHistoryItem> | null) => void;
};

export const BatteryVoltageChart = ({
    chartTitle,
    spotterId,
    alertLines,
    lines,
    onSelectData,
}: BatteryVoltageChartProps) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<DeviceStatusHistoryItem> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(null);
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    const timeZone = usePreferredTimeZone();

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `battery-voltage-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    useTrackChartView({
        containerEl,
        spotterId: spotterId!,
        chartTitle,
    });

    const { data: deviceStatusHistory, isPending } = useDeviceStatusHistory({
        spotterId: spotterId!,
        dateRange,
    });

    const drawChart = useCallback(drawLineChart, []);

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!deviceStatusHistory) {
            return;
        }

        const newChart = drawChart<DeviceStatusHistoryItem>({
            dataset: deviceStatusHistory,
            lines,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Battery voltage',
            yAxisOptions: {
                minRange: { yMin: 3, yMax: 4.5 },
            },
            containerId,
            showAlertLines: true,
            scaleChartToFitAllAlerts: false,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp: setSelectedTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        lines,
        onSelectData,
        deviceStatusHistory,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [deviceStatusHistory?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const BatteryVoltageSection = () => {
    const chartTitle = 'Battery history';
    const { spotterId } = useSpotterContext();
    const tz = useTzContext();
    const { data: device } = useDevice();
    const { data: systemConfigs } = useDeviceSystemConfigurations({ spotterId });
    const { data: systemErrors } = useDeviceSystemErrors({ spotterId });
    const isBridgeDisabled = systemErrors?.bridge?.code === 'WARN_LOW_BATT';
    const { bridgeDisableThresholdMV, bridgeReenableDeltaMV } = systemConfigs ?? {};

    const availableTimeframes: Timeframe[] = useMemo(
        () => [
            [3, 'days'],
            [7, 'days'],
            [1, 'months'],
            [6, 'months'],
        ],
        []
    );
    const [selectedData, setSelectedData] = useState<LineDataSlice<DeviceStatusHistoryItem> | null>(null);
    const [timeframe, setTimeframe] = useState<Timeframe>([7, 'days']);
    const endDate = useMemo(() => new Date(), []);
    const startDate = useMemo(() => {
        const [value, units] = timeframe;
        return sub(endDate, { [units]: value }, tz);
    }, [endDate, timeframe, tz]);
    const dateRange = { startDate, endDate };
    const { data: deviceStatusHistory, isPending } = useDeviceStatusHistory({ spotterId: spotterId!, dateRange });

    const alertLines = useMemo(() => {
        if (!device || !device.isSmartMooring || !deviceStatusHistory || !deviceStatusHistory.length) {
            return [];
        }

        if (!bridgeDisableThresholdMV) {
            return [];
        }

        const minBattV = deviceStatusHistory.toSorted((a, b) => a.batteryVoltage - b.batteryVoltage)[0].batteryVoltage;
        if (!isBridgeDisabled && minBattV > bridgeDisableThresholdMV / 1000) {
            // suppress threshold lines if the line never dropped below the disabled threshold (so that the "reenabled" line isn't confusing if it's not relevant)
            // UNLESS the bridge is currently disabled, in which case either the breach was further back in time than we see (but reenable V is still relevant),
            // or we just skimmed the threshold and the bridge got disabled but by the time the history was reported we were a few mV over the line.
            return [];
        }

        const alerts: AlertLine[] = [];

        alerts.push({
            value: bridgeDisableThresholdMV / 1000,
            operator: '<',
            label: 'Smart Mooring disabled',
            severity: 'error',
        });
        if (bridgeReenableDeltaMV) {
            alerts.push({
                value: (bridgeDisableThresholdMV + bridgeReenableDeltaMV) / 1000,
                operator: '>',
                label: 'Smart Mooring reenabled',
                severity: 'info',
            });
        }
        return alerts;
    }, [bridgeDisableThresholdMV, bridgeReenableDeltaMV, device, deviceStatusHistory, isBridgeDisabled]);

    return (
        <Stack gap={2} p={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={-2} mb={-1}>
                <SectionHeader title={chartTitle} variant="h5" />
                <Box sx={{ ml: 3 }}>
                    <TimeframeButtonGroup
                        currentTimeframe={timeframe}
                        availableTimeframes={availableTimeframes}
                        onTimeframeChange={setTimeframe}
                    />
                </Box>
            </Stack>
            <FixedDateRangeContextProvider startDate={startDate} endDate={endDate}>
                {isPending ? (
                    <GenericSkeleton />
                ) : deviceStatusHistory?.length ? (
                    <Box>
                        <ChartWithInfo
                            chart={
                                <BatteryVoltageChart
                                    lines={LINES}
                                    spotterId={spotterId!}
                                    chartTitle={chartTitle}
                                    onSelectData={setSelectedData}
                                    alertLines={alertLines}
                                />
                            }
                        >
                            <ChartDataDetails>
                                {!selectedData && (
                                    <LineChartDataSummary
                                        dateRange={dateRange}
                                        lines={LINES}
                                        dataset={deviceStatusHistory}
                                        renderLineSummary={() => (
                                            <>
                                                <DataLineMean />
                                                <DataLineExtent />
                                            </>
                                        )}
                                    />
                                )}
                                <DataDetailsByTimestamp
                                    selectedData={selectedData}
                                    datumRenderer={({ lines, datum }) => (
                                        <LineChartDatumDetail
                                            dataset={deviceStatusHistory}
                                            datum={datum}
                                            lines={lines}
                                        />
                                    )}
                                />
                            </ChartDataDetails>
                        </ChartWithInfo>
                    </Box>
                ) : (
                    <Alert severity="info">No battery voltage data for selected date range</Alert>
                )}
            </FixedDateRangeContextProvider>
        </Stack>
    );
};
